import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, Inbox, ShieldCheck, Package, TriangleAlert, MessageSquareWarning, Lightbulb, Link2, ChevronRight, Mail, Pause, Play, UserPlus, Send, RefreshCw, Check, X, Megaphone, ShoppingCart, BookOpen, Sparkles, Hammer, HelpCircle, Library, Folder, Wallet, Calculator, Video } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { SearchBox, Tabs } from "@/components/ContentParts";
import { inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";
import { usd } from "@/pages/store/Store";
import { SupportLoginButton } from "@/components/SupportLoginButton";

const useLoad = (path, pick = (r) => r) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => api(path).then((r) => setData(pick(r))).catch((e) => setErr(e.message)), [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return [data, load, err];
};
const doIt = async (fn, ok, after) => { try { await fn(); if (ok) toast.success(ok); after && (await after()); } catch (e) { toast.error(e.message); } };
const Pill = ({ children, color = "#888" }) => <span className="rounded-full px-2 py-0.5 text-[10px] font-extrabold" style={{ color, border: `1px solid ${color}` }}>{children}</span>;

const TOOLS = [
  ["/admin/console?tab=franchisee", "Franchisees", Users, "Roster · create · edit"],
  ["/admin/console?tab=franchisee&role=machine_owner", "Machine Owners", Users, "Add machine owners · grant LSO access"],
  ["/admin/console?tab=franchisee&role=rental_customer", "Rental Customers", Users, "Add rental customer accounts"],
  ["/admin/broadcast", "Broadcast", Megaphone, "Fleet-wide announcements"],
  ["/admin/quote-builder-tracker", "Quote Builder Adoption", Calculator, "Who saw What's New · who's using it"],
  ["/admin/jobber-fixes", "Jobber Import Corrections", Calculator, "Dry runs · review queue · approve & roll back"],
  ["/admin/quote-templates", "Quote Templates", Calculator, "ALR-approved, versioned quote wording"],
  ["/admin/store", "Store", ShoppingCart, "Products · pricing"],
  ["/admin/marketing-videos", "Marketing Videos", Video, "Connect Google Drive · franchisee uploads"],
  ["/admin/console?tab=kb", "AI Knowledge Base", BookOpen, "Coach training data"],
  ["/admin/console?tab=insights", "AI Insights", Sparkles, "Auto-generated"],
  ["/admin/console?tab=training", "Cleaning Settings", Hammer, "Power · pulse presets"],
  ["/admin/console?tab=lsoquiz", "LSO Quiz Editor", HelpCircle, "Safety cert questions"],
  ["/admin/console?tab=manual", "Ops Manual", Library, "PDF · release notes"],
  ["/admin/console?tab=resource", "Resource Hub", Folder, "Docs · templates"],
];

const SECTIONS = [
  ["users", "People & accounts", Users, (s) => `${s.franchise_pipeline?.franchisees_total ?? "—"} franchisees`],
  ["inbound", "Inbound email triage", Inbox, (s) => `${s.community_ops?.inbound_awaiting ?? 0} awaiting`],
  ["insurance", "Insurance reviews", ShieldCheck, (s) => `${s.community_ops?.insurance_pending ?? 0} pending`],
  ["orders", "Store orders", Package, () => "Status & tracking"],
  ["safety", "Safety incidents", TriangleAlert, () => "Incidents & checklists"],
  ["moderation", "Community moderation", MessageSquareWarning, (s) => `${s.community_ops?.community_flagged ?? 0} flagged`],
  ["feedback", "Feature suggestions", Lightbulb, (s) => `${s.community_ops?.feedback_new ?? 0} new`],
];

export function AdminHub() {
  const navigate = useNavigate();
  const [stats] = useLoad("/admin/dashboard-stats");
  return (
    <PortalPage eyebrow="CORPORATE ADMIN" title="Admin Hub" testId="admin-hub-page" width="max-w-5xl">
      <button onClick={() => navigate("/admin/billing")} data-testid="admin-invoicing-hero" className="flex w-full items-center gap-4 rounded-2xl border border-[var(--accent)] bg-[rgba(76,204,6,0.07)] p-5 text-left transition-colors hover:bg-[rgba(76,204,6,0.12)]">
        <Wallet size={30} className="shrink-0 text-[var(--accent)]" />
        <span className="flex-1"><span className="block text-lg font-extrabold">Franchisee Invoicing & Royalties</span><span className="text-xs text-neutral-400">On/off switches · each franchisee's account · log in as a franchisee · receipts · royalty ledger · monthly report</span></span>
        <ChevronRight size={20} className="text-[var(--accent)]" />
      </button>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SECTIONS.map(([key, title, Icon, sub]) => (
          <button key={key} onClick={() => navigate(`/admin/${key}`)} data-testid={`admin-section-${key}`} className="flex items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-5 text-left hover:border-[var(--accent)]">
            <Icon size={24} className="text-[var(--accent)]" />
            <span className="flex-1"><span className="block font-bold">{title}</span><span className="text-xs text-neutral-400">{stats ? sub(stats) : "…"}</span></span>
            <ChevronRight size={18} className="text-neutral-500" />
          </button>
        ))}
        <button onClick={() => navigate("/quickbooks")} data-testid="admin-section-quickbooks" className="flex items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-5 text-left hover:border-[var(--accent)]"><Link2 size={24} className="text-[var(--accent)]" /><span className="flex-1 font-bold">QuickBooks</span><ChevronRight size={18} className="text-neutral-500" /></button>
      </div>
      <p className="text-[11px] font-bold tracking-[3px] text-[var(--accent)]">ADMIN CONSOLE & TOOLS</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {TOOLS.map(([to, title, Icon, sub]) => (
          <button key={to} onClick={() => navigate(to)} data-testid={`admin-tool-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-5 text-left hover:border-[var(--accent)]">
            <Icon size={24} className="text-[var(--accent)]" />
            <span className="flex-1"><span className="block font-bold">{title}</span><span className="text-xs text-neutral-400">{sub}</span></span>
            <ChevronRight size={18} className="text-neutral-500" />
          </button>
        ))}
      </div>
      <p className="text-xs text-neutral-500">More admin tools from the current portal (territory, team training, default notices…) are still on the current portal.</p>
    </PortalPage>
  );
}

const ROLE_TABS = [["franchisee", "FRANCHISEES"], ["franchisee_employee", "EMPLOYEES"], ["machine_owner", "MACHINE OWNERS"], ["rental_customer", "RENTAL"], ["prospect", "PROSPECTS"]];

const AdminUsers = () => {
  const [role, setRole] = useState("franchisee");
  const [q, setQ] = useState("");
  const [users, load, err] = useLoad(`/admin/franchisees?role=${role}`);
  const [f, setF] = useState({ full_name: "", email: "", role: "franchisee" });
  const shown = useMemo(() => (users || []).filter((u) => !q.trim() || `${u.full_name} ${u.email}`.toLowerCase().includes(q.trim().toLowerCase())), [users, q]);
  const create = (e) => { e.preventDefault(); doIt(() => api("/admin/franchisees", { method: "POST", body: { ...f, email: f.email.trim(), full_name: f.full_name.trim() } }), `Account created — setup link emailed to ${f.email}`, async () => { setF({ ...f, full_name: "", email: "" }); await load(); }); };
  return (
    <>
      <Card>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-4" data-testid="admin-create-user">
          <input data-testid="admin-new-name" className={inputCls} placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
          <input data-testid="admin-new-email" type="email" className={inputCls} placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <select data-testid="admin-new-role" className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="franchisee">Franchisee</option><option value="machine_owner">Machine owner</option><option value="rental_customer">Rental customer</option></select>
          <Btn type="submit" disabled={!f.email.includes("@")} data-testid="admin-new-submit"><UserPlus size={16} /> Create</Btn>
        </form>
      </Card>
      <Tabs tabs={ROLE_TABS.map(([key, label]) => ({ key, label }))} active={role} onChange={setRole} />
      <SearchBox value={q} onChange={setQ} placeholder="Search name or email…" testId="admin-user-search" />
      <ErrorNote>{err}</ErrorNote>
      {!users && !err && <Spinner />}
      {users && <p className="text-xs text-neutral-500" data-testid="admin-user-count">{shown.length} account{shown.length === 1 ? "" : "s"}</p>}
      {shown.map((u) => (
        <Card key={u.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-user-${u.id}`}>
          <div className="min-w-[200px] flex-1"><p className="font-semibold">{u.full_name || "—"} {u.is_active === false && <Pill color="#FF3333">SUSPENDED</Pill>}</p><p className="text-xs text-neutral-400">{u.email}</p></div>
          {u.role === "franchisee" && u.is_active !== false && <SupportLoginButton franchisee={u} to="/dashboard" compact />}
          <button title="Email a set-password link" onClick={() => window.confirm(`Email a set-password link to ${u.email}?`) && doIt(() => api(`/admin/franchisees/${u.id}/send-setup-link`, { method: "POST" }), "Setup link sent")} data-testid={`admin-user-link-${u.id}`} className="rounded-lg border border-[#333] p-2 hover:text-[var(--accent)]"><Mail size={16} /></button>
          {["franchisee", "franchisee_employee"].includes(u.role) && (
            <button title={u.is_active === false ? "Unsuspend" : "Suspend"} onClick={() => window.confirm(`${u.is_active === false ? "Unsuspend" : "Suspend"} ${u.email}?`) && doIt(() => api(`/admin/franchisees/${u.id}/suspend`, { method: "PATCH", body: { is_suspended: u.is_active !== false } }), "Updated", load)} data-testid={`admin-user-suspend-${u.id}`} className="rounded-lg border border-[#333] p-2 hover:text-[#F5A623]">{u.is_active === false ? <Play size={16} /> : <Pause size={16} />}</button>
          )}
        </Card>
      ))}
    </>
  );
};

const LEAD_TABS = [["awaiting_approval", "AWAITING"], ["sent", "SENT"], ["", "ALL"]];
const AdminInbound = () => {
  const [status, setStatus] = useState("awaiting_approval");
  const [leads, load, err] = useLoad(`/inbound/leads${status ? `?status=${status}` : ""}`, (r) => r.leads || []);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const openLead = (id) => api(`/inbound/leads/${id}`).then(setOpen).catch((e) => toast.error(e.message));
  const save = () => doIt(() => api(`/inbound/leads/${open.id}`, { method: "PATCH", body: { draft_subject: open.draft_subject, draft_html: open.draft_html } }), "Draft saved");
  const send = async () => {
    if (!window.confirm(`Send this reply to ${open.ai_suggested_reply_target || open.from}?`)) return;
    setBusy("send");
    await doIt(async () => { await api(`/inbound/leads/${open.id}`, { method: "PATCH", body: { draft_subject: open.draft_subject, draft_html: open.draft_html } }); await api(`/inbound/leads/${open.id}/approve`, { method: "POST" }); }, "Reply sent", async () => { setOpen(null); await load(); });
    setBusy(null);
  };
  const regen = async () => { setBusy("regen"); await doIt(async () => setOpen(await api(`/inbound/leads/${open.id}/regenerate`, { method: "POST" })), "New draft generated"); setBusy(null); };

  if (open) return (
    <Card className="space-y-3" data-testid="inbound-detail">
      <button onClick={() => setOpen(null)} className="text-sm font-bold text-neutral-400" data-testid="inbound-back">‹ All leads</button>
      <p className="font-display text-2xl font-bold">{open.subject}</p>
      <p className="text-xs text-neutral-400">From {open.from_name} &lt;{open.from}&gt; · {fmtDate(open.received_at)} · {open.lead_type}{open.customer_phone ? ` · ${open.customer_phone}` : ""}</p>
      {open.ai_safe_to_send === false && <p className="rounded-lg border border-[#F5A623] p-2 text-xs text-[#F5A623]">AI flagged: {open.ai_safety_notes}</p>}
      <details className="rounded-lg border border-[#333] p-3 text-sm text-neutral-300"><summary className="cursor-pointer font-bold">Original message</summary><p className="mt-2 whitespace-pre-line">{open.stripped_text_reply || open.text_body}</p></details>
      <input data-testid="inbound-subject" className={inputCls} value={open.draft_subject || ""} onChange={(e) => setOpen({ ...open, draft_subject: e.target.value })} />
      <textarea data-testid="inbound-draft" rows={10} className={`${inputCls} font-mono text-xs`} value={open.draft_html || ""} onChange={(e) => setOpen({ ...open, draft_html: e.target.value })} />
      <div className="rounded-lg bg-white p-4 text-sm text-black" data-testid="inbound-preview" dangerouslySetInnerHTML={{ __html: open.draft_html || "" }} />
      <p className="text-xs text-neutral-500">Will send to: <b className="text-white">{open.ai_suggested_reply_target || open.from}</b></p>
      <div className="grid gap-2 sm:grid-cols-3">
        <Btn variant="outline" onClick={save} data-testid="inbound-save">Save draft</Btn>
        <Btn variant="outline" busy={busy === "regen"} onClick={regen} data-testid="inbound-regenerate"><RefreshCw size={14} /> Regenerate</Btn>
        <Btn busy={busy === "send"} disabled={open.status === "sent"} onClick={send} data-testid="inbound-send"><Send size={14} /> {open.status === "sent" ? "Sent" : "Approve & send"}</Btn>
      </div>
    </Card>
  );
  return (
    <>
      <Tabs tabs={LEAD_TABS.map(([key, label]) => ({ key, label }))} active={status} onChange={setStatus} />
      <ErrorNote>{err}</ErrorNote>
      {!leads && !err && <Spinner />}
      {leads?.length === 0 && <p className="text-sm text-neutral-400" data-testid="inbound-empty">No leads here.</p>}
      {leads?.map((l) => (
        <button key={l.id} onClick={() => openLead(l.id)} data-testid={`inbound-lead-${l.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{l.subject}</span><span className="text-xs text-neutral-400">{l.from_name || l.from} · {l.lead_type} · {fmtDate(l.received_at)}</span></span>
          <Pill color={l.status === "sent" ? "#4CCC06" : "#F5A623"}>{(l.status || "").toUpperCase()}</Pill>
        </button>
      ))}
    </>
  );
};

const AdminInsurance = () => {
  const [rows, load, err] = useLoad("/insurance/admin/all", (r) => r.users || r || []);
  const act = (r, kind) => doIt(() => api(`/insurance/admin/${kind}?user_id=${encodeURIComponent(r.id)}`, { method: "POST" }), kind === "approve" ? "Approved" : "Rejected", load);
  return (
    <>
      <ErrorNote>{err}</ErrorNote>
      {!rows && !err && <Spinner />}
      {rows?.sort((a, b) => (a.status === "pending_review" ? -1 : 1) - (b.status === "pending_review" ? -1 : 1)).map((r) => (
        <Card key={r.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`admin-ins-${r.id}`}>
          <div className="min-w-[200px] flex-1"><p className="font-semibold">{r.full_name || r.email}</p><p className="text-xs text-neutral-400">{r.filename || "No policy"}{r.expires_at ? ` · expires ${fmtDate(r.expires_at)}` : ""}{r.pending_filename ? ` · pending: ${r.pending_filename}` : ""}</p></div>
          <Pill color={r.status === "ok" ? "#4CCC06" : r.status === "default" ? "#FF3333" : "#F5A623"}>{(r.status || "").toUpperCase()}</Pill>
          {r.status === "pending_review" && <><button onClick={() => act(r, "approve")} data-testid={`admin-ins-approve-${r.id}`} className="rounded-lg border border-[#333] p-2 text-[var(--accent)]"><Check size={16} /></button><button onClick={() => act(r, "reject")} data-testid={`admin-ins-reject-${r.id}`} className="rounded-lg border border-[#333] p-2 text-[#FF3333]"><X size={16} /></button></>}
        </Card>
      ))}
    </>
  );
};

const AdminOrders = () => {
  const [orders, load, err] = useLoad("/store/orders");
  const update = (o, status) => {
    const tracking = status === "shipped" ? window.prompt("Tracking number (optional)", o.tracking_number || "") : null;
    const qs = new URLSearchParams({ status, ...(tracking ? { tracking_number: tracking } : {}) });
    doIt(() => api(`/store/orders/${o.id}/status?${qs}`, { method: "PATCH" }), "Order updated", load);
  };
  return (
    <>
      <ErrorNote>{err}</ErrorNote>
      {!orders && !err && <Spinner />}
      {orders?.length === 0 && <p className="text-sm text-neutral-400" data-testid="orders-empty">No orders yet.</p>}
      {orders?.map((o) => (
        <Card key={o.id} className="p-4" data-testid={`admin-order-${o.id}`}>
          <div className="flex flex-wrap justify-between gap-2"><p className="font-semibold">#{o.qbo_invoice_doc_number || o.id.slice(0, 8)} · {o.shipping_info?.business_name || o.user_email}</p><Pill>{o.status.toUpperCase()}</Pill></div>
          <p className="text-xs text-neutral-400">{fmtDate(o.created_at)} · {o.items.length} item(s) · {usd(o.total_usd)}{o.tracking_number ? ` · ${o.tracking_number}` : ""}</p>
          <div className="mt-2 flex flex-wrap gap-1">{["paid", "shipped", "delivered", "canceled"].map((s) => <button key={s} onClick={() => update(o, s)} data-testid={`admin-order-${s}-${o.id}`} className="rounded-lg border border-[#333] px-2 py-1 text-[11px] font-bold hover:border-[var(--accent)]">{s}</button>)}</div>
        </Card>
      ))}
    </>
  );
};

const AdminSafety = () => {
  const [items, load, err] = useLoad("/safety/admin/incidents", (r) => r.incidents || r || []);
  return (
    <>
      <ErrorNote>{err}</ErrorNote>
      {!items && !err && <Spinner />}
      {items?.length === 0 && <p className="text-sm text-neutral-400">No incidents reported.</p>}
      {items?.map((i) => (
        <Card key={i.id} className="p-4" data-testid={`admin-incident-${i.id}`}>
          <div className="flex justify-between gap-2"><p className="font-semibold">{i.incident_type_title}</p><Pill color={i.status === "closed" ? "#22C55E" : "#F97316"}>{i.status.toUpperCase()}</Pill></div>
          <p className="text-xs text-neutral-400">{i.submitter_name || i.user_email} · {i.severity} · {i.location} · {fmtDate(i.created_at)}</p>
          <div className="mt-2 flex gap-1">{["open", "under_review", "closed"].map((s) => <button key={s} disabled={i.status === s} onClick={() => doIt(() => api(`/safety/admin/incidents/${i.id}/status`, { method: "POST", body: { status: s } }), "Status updated", load)} data-testid={`admin-incident-${s}-${i.id}`} className="rounded-lg border border-[#333] px-2 py-1 text-[11px] font-bold disabled:opacity-40">{s.replace("_", " ")}</button>)}</div>
        </Card>
      ))}
    </>
  );
};

const AdminModeration = () => {
  const [q, load, err] = useLoad("/customer-community/moderation/queue");
  const items = q ? [...q.posts.map((p) => ({ ...p, kind: "post" })), ...q.replies.map((r) => ({ ...r, kind: "reply" }))] : null;
  return (
    <>
      <ErrorNote>{err}</ErrorNote>
      {!items && !err && <Spinner />}
      {items?.length === 0 && <p className="text-sm text-neutral-400" data-testid="moderation-empty">Nothing waiting for review.</p>}
      {items?.map((it) => (
        <Card key={it.id} className="p-4" data-testid={`mod-${it.kind}-${it.id}`}>
          <p className="text-xs text-neutral-400">{it.kind.toUpperCase()} · {it.author?.first_name} · {fmtDate(it.created_at)}{it.moderation?.reason ? ` · AI: ${it.moderation.reason}` : ""}</p>
          {it.title && <p className="font-semibold">{it.title}</p>}
          <p className="mt-1 whitespace-pre-line text-sm">{it.body}</p>
          <div className="mt-2 flex gap-2">
            {["approve", "reject"].map((a) => <button key={a} onClick={() => doIt(() => api(`/customer-community/moderation/${it.kind === "post" ? "posts" : "replies"}/${it.id}/${a}`, { method: "POST", body: {} }), a === "approve" ? "Approved" : "Rejected", load)} data-testid={`mod-${a}-${it.id}`} className={`rounded-lg border px-3 py-1 text-xs font-bold ${a === "approve" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#FF3333] text-[#FF3333]"}`}>{a}</button>)}
          </div>
        </Card>
      ))}
    </>
  );
};

const AdminFeedback = () => {
  const [items, , err] = useLoad("/feedback");
  return (
    <>
      <ErrorNote>{err}</ErrorNote>
      {!items && !err && <Spinner />}
      {items?.length === 0 && <p className="text-sm text-neutral-400" data-testid="feedback-empty">No suggestions yet.</p>}
      {items?.map((it) => (
        <Card key={it.id} className="p-4" data-testid={`admin-feedback-${it.id}`}>
          <div className="flex justify-between gap-2"><p className="font-semibold">{it.title || it.summary || "Suggestion"}</p><Pill>{(it.status || "new").toUpperCase()}</Pill></div>
          <p className="mt-1 whitespace-pre-line text-sm text-neutral-300">{it.body || it.description || it.text}</p>
          <p className="mt-1 text-xs text-neutral-500">{it.user_full_name || it.user_email} · {it.user_role} · {fmtDate(it.created_at)} · {(it.upvoted_by || []).length} upvotes</p>
        </Card>
      ))}
    </>
  );
};

const VIEWS = { users: AdminUsers, inbound: AdminInbound, insurance: AdminInsurance, orders: AdminOrders, safety: AdminSafety, moderation: AdminModeration, feedback: AdminFeedback };

export function AdminSection() {
  const { section } = useParams();
  const View = VIEWS[section];
  const title = SECTIONS.find(([k]) => k === section)?.[1] || "Admin";
  return (
    <PortalPage eyebrow="ADMIN HUB" title={title} back="/admin" backLabel="Admin Hub" testId={`admin-${section}-page`} width="max-w-5xl">
      {View ? <View /> : <p className="text-sm text-neutral-400" data-testid="admin-not-ported">This admin tool is still on the current portal.</p>}
    </PortalPage>
  );
}
